import { Component, OnInit } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MdbModalRef } from 'mdb-angular-ui-kit/modal';
import { finalize } from 'rxjs/operators';
import { MESSAGE_CONSTANTS, STATUS_CODES } from 'src/app/constants';
import { ThreatPairApi } from 'src/app/components/common/threat-pair-table/threat-pair-table.model';
import { ThreatPairService, ToasterService } from 'src/app/services';
import { NightwebUtil } from 'src/app/utils';
import { RoleCode } from 'src/app/enums';
import { NOTIFICATION_CHANNEL } from 'src/app/constants/notification-channel.constant';

export type ThreatPairSelectOption = {
  label: string;
  value: string;
  organizationId: string;
};

function nonEmptyArrayValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const v = control.value;
    if (!Array.isArray(v) || v.length === 0) {
      return { required: true };
    }
    return null;
  };
}

/** Yellow (early warning) perimeter must be greater than red (critical) perimeter. */
function yellowGreaterThanRedValidator(): ValidatorFn {
  return (group: AbstractControl): ValidationErrors | null => {
    const yellow = Number(group.get('distanceThreshold')?.value);
    const red = Number(group.get('redDistanceThreshold')?.value);
    if (!Number.isFinite(yellow) || !Number.isFinite(red)) {
      return null;
    }
    return yellow > red ? null : { yellowGreaterThanRed: true };
  };
}

@Component({
    selector: 'app-add-edit-threat-pair-modal',
    templateUrl: './add-edit-threat-pair-modal.component.html',
    standalone: false
})
export class AddEditThreatPairModalComponent implements OnInit {
  form!: FormGroup;
  submitted = false;
  loading = false;

  isEdit = false;
  editThreatPair: ThreatPairApi | null = null;

  organizationList: ThreatPairSelectOption[] = [];
  survivorUserList: ThreatPairSelectOption[] = [];
  perpetratorUserList: ThreatPairSelectOption[] = [];
  backupEndUserList: {
    survivorList: ThreatPairSelectOption[];
    perpetratorList: ThreatPairSelectOption[];
  } = {
    survivorList: [],
    perpetratorList: [],
  };

  organizationSelectLocked = false;

  /** Hidden for Client Admin and Advocate; only other roles see “View as Advocate”. */
  get showViewAsAdvocateSection(): boolean {
    const code = this.nightwebUtil.getRole();
    return code !== RoleCode.ADMIN && code !== RoleCode.ADVOCATE;
  }

  defaultYellowDistanceThreshold = 300;
  defaultRedDistanceThreshold = 150;
  readonly defaultYellowCheckInTime = 3;
  readonly defaultRedCheckInTime = 1;
  defaultDistanceParameter = 'feet';

  readonly distanceParameterOptions: { label: string; value: string }[] = [
    { label: 'Feet', value: 'feet' },
    { label: 'Yard', value: 'yard' },
    { label: 'Mile', value: 'mile' },
  ];

  get selectedDistanceParameterLabel(): string {
    const value =
      this.form?.get('distanceParameter')?.value ??
      this.defaultDistanceParameter;
    return (
      this.distanceParameterOptions.find((o) => o.value === value)?.label ??
      'Feet'
    );
  }

  /** Checkbox UI with radio behavior — exactly one unit stays selected. */
  onDistanceParameterChange(value: string): void {
    this.form.get('distanceParameter')?.setValue(value);
  }

  private readonly statusCodes = STATUS_CODES;
  private threatPairId: string | null = null;
  private readonly notificationChannels = NOTIFICATION_CHANNEL;

  constructor(
    public modalRef: MdbModalRef<AddEditThreatPairModalComponent>,
    private formBuilder: FormBuilder,
    private threatPairService: ThreatPairService,
    private toasterService: ToasterService,
    private nightwebUtil: NightwebUtil,
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    this.backupEndUserList.survivorList = JSON.parse(
      JSON.stringify(this.survivorUserList),
    );
    this.backupEndUserList.perpetratorList = JSON.parse(
      JSON.stringify(this.perpetratorUserList),
    );

    if (this.isEdit && this.editThreatPair) {
      this.threatPairId = this.editThreatPair.id;
      const ch = this.editThreatPair.notificationChannels ?? [
        NOTIFICATION_CHANNEL.PUSH,
      ];
      this.form.patchValue({
        distanceParameter:
          this.editThreatPair.distanceParameter ??
          this.defaultDistanceParameter,
        distanceThreshold: this.editThreatPair.distanceThreshold,
        yellowCheckInTime: this.editThreatPair.yellowCheckInTime,
        redDistanceThreshold: this.editThreatPair.redDistanceThreshold,
        redCheckInTime: this.editThreatPair.redCheckInTime,
        organizationId: this.editThreatPair.organizationId,
        survivorId: this.editThreatPair.survivorId,
        perpetratorIds: [this.editThreatPair.perpetratorId],
        notificationEmail: ch.includes(NOTIFICATION_CHANNEL.EMAIL),
        notificationSms: ch.includes(NOTIFICATION_CHANNEL.SMS),
      });
      this.form.get('survivorId')?.disable();
      this.form.get('perpetratorIds')?.disable();
      this.form.get('organizationId')?.disable();
      return;
    }

    if (this.shouldAutoPatchOrganizationForAdd()) {
      const orgId = this.organizationList[0].value;
      if (orgId) {
        this.form.patchValue({ organizationId: orgId });
        this.organizationSelectLocked = true;
      }
    }
  }

  onOrganizationChange(event: any): void {
    if (event?.value) {
      this.survivorUserList = this.backupEndUserList.survivorList.filter(
        (user) => user.organizationId == event.value,
      );
      this.perpetratorUserList = this.backupEndUserList.perpetratorList.filter(
        (user) => user.organizationId == event.value,
      );
      this.form.patchValue({
        survivorId: null,
        perpetratorIds: [],
      });
    }
  }

  private shouldAutoPatchOrganizationForAdd(): boolean {
    const code = this.nightwebUtil.getRole();
    return code == RoleCode.ADMIN || code === RoleCode.ADVOCATE;
  }

  buildForm(): void {
    this.form = this.formBuilder.group(
      {
        organizationId: [null, Validators.required],
        survivorId: [null, Validators.required],
        perpetratorIds: [[], [nonEmptyArrayValidator()]],
        distanceParameter: [
          this.defaultDistanceParameter,
          Validators.required,
        ],
        distanceThreshold: [
          this.defaultYellowDistanceThreshold,
          [Validators.required, Validators.min(0.1)],
        ],
        yellowCheckInTime: [
          {
            value: this.defaultYellowCheckInTime,
          },
          [Validators.required, Validators.min(0.1)],
        ],
        redDistanceThreshold: [
          this.defaultRedDistanceThreshold,
          [Validators.required, Validators.min(0.1)],
        ],
        redCheckInTime: [
          {
            value: this.defaultRedCheckInTime
          },
          [Validators.required, Validators.min(0.1)],
        ],
        notificationPush: [{ value: true, disabled: true }],
        notificationEmail: [false],
        notificationSms: [false],
      },
      { validators: [yellowGreaterThanRedValidator()] },
    );
  }

  close(): void {
    if (this.loading) {
      return;
    }
    this.modalRef.close();
  }

  save(): void {
    this.submitted = true;
    if (this.isEdit && this.threatPairId) {
      this.updateThreatPair();
    } else {
      this.addThreatPair();
    }
  }

  private buildNotificationChannelsPayload(payload: any): string[] {
    const notificationChannels = [
      this.notificationChannels.PUSH,
      ...(payload.notificationEmail ? [this.notificationChannels.EMAIL] : []),
      ...(payload.notificationSms ? [this.notificationChannels.SMS] : []),
    ];
    delete payload.notificationPush;
    delete payload.notificationEmail;
    delete payload.notificationSms;
    return notificationChannels;
  }

  private addThreatPair(): void {
    const survivorId = this.form.get('survivorId')?.value;
    const perpetrators = this.form.get('perpetratorIds')?.value;
    const hasSurvivor =
      survivorId != null && String(survivorId).trim().length > 0;
    const hasPerpetrators = Array.isArray(perpetrators)
      ? perpetrators.length > 0
      : perpetrators != null && String(perpetrators).length > 0;
    if (this.form.invalid || !hasSurvivor || !hasPerpetrators) {
      return;
    }

    const payload = this.form.getRawValue();
    payload.notificationChannels =
      this.buildNotificationChannelsPayload(payload);

    this.loading = true;

    this.threatPairService
      .createThreatPairs(payload)
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.statusCode == this.statusCodes.OK) {
            this.toasterService.show(
              'Success',
              MESSAGE_CONSTANTS.THREAT_PAIRS_CREATED,
            );
            this.modalRef.close(response.data ?? true);
          }
        },
        error: () => {},
      });
  }

  private updateThreatPair(): void {
    if (!this.threatPairId) {
      return;
    }
    if (this.form.invalid) {
      return;
    }

    this.loading = true;
    const notificationChannels = this.buildNotificationChannelsPayload(this.form.getRawValue());
    this.threatPairService
      .patchThreatPair(this.threatPairId, {
        survivorId: this.form.get('survivorId')?.value,
        perpetratorIds: this.form.get('perpetratorIds')?.value,
        distanceParameter: this.form.get('distanceParameter')?.value,
        distanceThreshold: Number(this.form.get('distanceThreshold')?.value),
        yellowCheckInTime: Number(this.form.get('yellowCheckInTime')?.value),
        redDistanceThreshold: Number(this.form.get('redDistanceThreshold')?.value),
        redCheckInTime: Number(this.form.get('redCheckInTime')?.value),
        notificationChannels: notificationChannels as string[],
      })
      .pipe(
        finalize(() => {
          this.loading = false;
        }),
      )
      .subscribe({
        next: (response: any) => {
          if (response?.statusCode == this.statusCodes.OK) {
            this.toasterService.show(
              'Success',
              MESSAGE_CONSTANTS.THREAT_PAIR_UPDATED,
            );
            this.modalRef.close(response.data ?? true);
          }
        },
        error: () => {},
      });
  }
}
